import { Item, StoreDetails, DeliveryCharge, Discount } from "../types";

/**
 * ESC/POS Command Constants
 */
const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;
// Removed unused constants
// const FS = 0x1c;
// const US = 0x1f;

/**
 * Formats text as centered
 */
export const textCenter = (): Uint8Array => {
    return new Uint8Array([ESC, 0x61, 0x01]);
};

/**
 * Builds a follow footer raster: left column (wider) with message text, right column (narrower) with QR image.
 */
export const buildFollowFooterRaster = async (
    message: string,
    qrUrl: string,
    options?: {
        totalWidth?: number;
        rightRatio?: number;
        qrMaxSize?: number;
        fontSize?: number;
        lineGap?: number;
        textTopPadding?: number;
    }
): Promise<Uint8Array | null> => {
    const totalWidth = options?.totalWidth ?? 576;
    const rightRatio = options?.rightRatio ?? 0.3; // QR column
    const qrMaxSize = options?.qrMaxSize ?? 120; // pixels
    const fontSize = options?.fontSize ?? 18;
    const lineGap = options?.lineGap ?? 4;
    const textTopPadding = options?.textTopPadding ?? 4;

    try {
        // Load QR image
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = (e) => reject(e);
            image.src = qrUrl;
        });

        // Column widths
        const rightWidth = Math.floor(totalWidth * rightRatio);
        const leftWidth = totalWidth - rightWidth - 6; // gutter

        // Scale QR
        const qrScale = Math.min(
            1,
            qrMaxSize / (img.naturalWidth || img.width || qrMaxSize),
            qrMaxSize / (img.naturalHeight || img.height || qrMaxSize)
        );
        const qrW = Math.floor((img.naturalWidth || img.width) * qrScale);
        const qrH = Math.floor((img.naturalHeight || img.height) * qrScale);

        // Prepare text wrapping on left
        const temp = document.createElement("canvas");
        const tctx = temp.getContext("2d");
        if (!tctx) return null;
        tctx.font = `${fontSize}px sans-serif`;

        const words = message.split(/\s+/);
        const lines: string[] = [];
        let current = "";
        for (const w of words) {
            const test = current ? current + " " + w : w;
            const width = tctx.measureText(test).width;
            if (width <= leftWidth) {
                current = test;
            } else {
                if (current) lines.push(current);
                // If single word too long, break it
                if (tctx.measureText(w).width > leftWidth) {
                    let partial = "";
                    for (const ch of w) {
                        const test2 = partial + ch;
                        if (tctx.measureText(test2).width <= leftWidth) {
                            partial = test2;
                        } else {
                            lines.push(partial);
                            partial = ch;
                        }
                    }
                    current = partial;
                } else {
                    current = w;
                }
            }
        }
        if (current) lines.push(current);

        // Measure text height
        let textHeight = 0;
        for (const line of lines) {
            const metrics = tctx.measureText(line);
            const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.8;
            const descent = metrics.actualBoundingBoxDescent || fontSize * 0.2;
            textHeight += ascent + descent + lineGap;
        }

        const canvasHeight = Math.max(qrH, textHeight) + 6;
        const canvas = document.createElement("canvas");
        canvas.width = totalWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, totalWidth, canvasHeight);

        // Draw text left
        ctx.fillStyle = "#000000";
        ctx.font = `${fontSize}px sans-serif`;
        let y = textTopPadding;
        for (const line of lines) {
            const m = ctx.measureText(line);
            const ascent = m.actualBoundingBoxAscent || fontSize * 0.8;
            const descent = m.actualBoundingBoxDescent || fontSize * 0.2;
            ctx.fillText(line, 0, y + ascent);
            y += ascent + descent + lineGap;
        }

        // Draw QR right (right aligned)
        const qrX = totalWidth - qrW;
        const qrY = Math.floor((canvasHeight - qrH) / 2);
        ctx.drawImage(img, qrX, qrY, qrW, qrH);

        return canvasToEscPosRaster(canvas);
    } catch (e) {
        console.error("Failed to build follow footer raster:", e);
        return null;
    }
};
/**
 * Builds a two-column header raster: small logo on the left, store details (name, address, phone, email, website)
 * on the right. Returns ESC/POS raster bytes suitable to prepend to the receipt.
 */
export const buildReceiptHeaderRaster = async (
    store: StoreDetails,
    logoUrl: string,
    options?: {
        totalWidth?: number;
        leftRatio?: number;
        logoMaxHeight?: number;
        nameFontSize?: number;
        lineFontSize?: number;
    }
): Promise<Uint8Array | null> => {
    const totalWidth = options?.totalWidth ?? 384; // typical 48mm@8dots/mm = 384, or 80mm can be 576
    const leftRatio = options?.leftRatio ?? 0.25; // 25% for logo column
    const logoMaxHeight = options?.logoMaxHeight ?? 60; // pixels
    const nameFontSize = options?.nameFontSize ?? 30;
    const lineFontSize = options?.lineFontSize ?? 12;

    try {
        // Load logo image
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = (e) => reject(e);
            image.src = logoUrl;
        });

        // Prepare a temporary canvas to measure text and compute final height
        const temp = document.createElement("canvas");
        const tctx = temp.getContext("2d");
        if (!tctx) return null;

        // Text styles
        const nameFont = `bold ${nameFontSize}px sans-serif`;
        const lineFont = `${lineFontSize}px sans-serif`;
        const lineGap = 4; // px

        // Columns
        const leftWidth = Math.floor(totalWidth * leftRatio);
        // const rightWidth = totalWidth - leftWidth - 6; // small gutter

        // Prepare text lines
        const lines: string[] = [];
        lines.push(store.name);
        if (store.address) lines.push(store.address);
        if (store.phone) lines.push(store.phone);
        if (store.email) lines.push(store.email);
        if (store.website) lines.push(store.website);

        // Measure text height
        let textHeight = 0;
        lines.forEach((line, idx) => {
            tctx.font = idx === 0 ? nameFont : lineFont;
            const metrics = tctx.measureText(line);
            const fallbackAscent =
                idx === 0 ? nameFontSize * 0.8 : lineFontSize * 0.8;
            const fallbackDescent =
                idx === 0 ? nameFontSize * 0.2 : lineFontSize * 0.2;
            const ascent = metrics.actualBoundingBoxAscent || fallbackAscent;
            const descent = metrics.actualBoundingBoxDescent || fallbackDescent;
            textHeight += ascent + descent + lineGap;
        });

        // Compute logo draw size
        const logoScale = Math.min(
            1,
            leftWidth / (img.naturalWidth || img.width || leftWidth),
            logoMaxHeight / (img.naturalHeight || img.height || logoMaxHeight)
        );
        const logoW = Math.floor((img.naturalWidth || img.width) * logoScale);
        const logoH = Math.floor((img.naturalHeight || img.height) * logoScale);

        const canvasHeight = Math.max(logoH, textHeight) + 6; // padding

        // Final canvas
        const canvas = document.createElement("canvas");
        canvas.width = totalWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        // White background
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw logo (left column), centered vertically
        const logoX = Math.floor((leftWidth - logoW) / 2);
        const logoY = Math.floor((canvasHeight - logoH) / 2);
        ctx.drawImage(img, logoX, logoY, logoW, logoH);

        // Draw text (right column)
        ctx.fillStyle = "#000000";
        let y = 4; // top padding
        lines.forEach((line, idx) => {
            ctx.font = idx === 0 ? nameFont : lineFont;
            const metrics = ctx.measureText(line);
            const fallbackAscent =
                idx === 0 ? nameFontSize * 0.8 : lineFontSize * 0.8;
            const fallbackDescent =
                idx === 0 ? nameFontSize * 0.2 : lineFontSize * 0.2;
            const ascent = metrics.actualBoundingBoxAscent || fallbackAscent;
            const descent = metrics.actualBoundingBoxDescent || fallbackDescent;
            // Left align text within right column
            const x = leftWidth + 6; // gutter
            // Draw baseline at y + ascent
            ctx.fillText(line, x, y + ascent);
            y += ascent + descent + lineGap; // advance by measured height + gap
        });

        // Convert to ESC/POS raster
        return canvasToEscPosRaster(canvas);
    } catch (e) {
        console.error("Failed to build header raster:", e);
        return null;
    }
};

/**
 * Converts an HTMLImageElement drawn to canvas into ESC/POS raster image bytes
 * using the 'GS v 0' command. The image is converted to monochrome.
 */
const canvasToEscPosRaster = (canvas: HTMLCanvasElement): Uint8Array => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return new Uint8Array();
    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height).data;

    // Each row packs 8 pixels per byte
    const bytesPerRow = Math.ceil(width / 8);
    const raster = new Uint8Array(bytesPerRow * height);

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4;
            const r = imageData[idx];
            const g = imageData[idx + 1];
            const b = imageData[idx + 2];
            // Simple luminance threshold
            const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
            const bit = luminance < 128 ? 1 : 0; // 1 = black pixel
            const byteIndex = y * bytesPerRow + Math.floor(x / 8);
            const bitIndex = 7 - (x % 8);
            if (bit) raster[byteIndex] |= 1 << bitIndex;
        }
    }

    // Build GS v 0 command
    // m = 0 (normal), xL/xH = width in bytes, yL/yH = height in pixels
    const xL = bytesPerRow & 0xff;
    const xH = (bytesPerRow >> 8) & 0xff;
    const yL = height & 0xff;
    const yH = (height >> 8) & 0xff;
    const header = new Uint8Array([GS, 0x76, 0x30, 0x00, xL, xH, yL, yH]);
    return concatUint8Arrays(header, raster, lineFeed());
};

/**
 * Loads an image from URL, scales it to maxWidth (preserving aspect), converts to monochrome,
 * and returns ESC/POS raster bytes. Returns null on failure.
 */
export const loadLogoAsEscPos = async (
    url: string,
    maxWidth = 360
): Promise<Uint8Array | null> => {
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = (e) => reject(e);
            image.src = url;
        });

        // Scale preserving aspect ratio
        const scale = Math.min(
            1,
            maxWidth / img.naturalWidth || img.width || 1
        );
        const width = Math.max(
            1,
            Math.floor((img.naturalWidth || img.width) * scale)
        );
        const height = Math.max(
            1,
            Math.floor((img.naturalHeight || img.height) * scale)
        );

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0, width, height);

        // Optional: convert to grayscale to improve thresholding
        const imgData = ctx.getImageData(0, 0, width, height);
        const d = imgData.data;
        for (let i = 0; i < d.length; i += 4) {
            const r = d[i],
                g = d[i + 1],
                b = d[i + 2];
            const gray = 0.299 * r + 0.587 * g + 0.114 * b;
            d[i] = d[i + 1] = d[i + 2] = gray;
        }
        ctx.putImageData(imgData, 0, 0);

        return canvasToEscPosRaster(canvas);
    } catch (e) {
        console.error("Failed to load/convert logo for ESC/POS:", e);
        return null;
    }
};

/**
 * Formats text as left-aligned
 */
export const textLeft = (): Uint8Array => {
    return new Uint8Array([ESC, 0x61, 0x00]);
};

/**
 * Sets text to bold
 */
export const textBold = (): Uint8Array => {
    return new Uint8Array([ESC, 0x45, 0x01]);
};

/**
 * Cancels bold text
 */
export const textNormal = (): Uint8Array => {
    return new Uint8Array([ESC, 0x45, 0x00]);
};

/**
 * Sets double height text
 */
export const textDoubleHeight = (): Uint8Array => {
    return new Uint8Array([ESC, 0x21, 0x10]);
};

/**
 * Sets double width text
 */
export const textDoubleWidth = (): Uint8Array => {
    return new Uint8Array([ESC, 0x21, 0x20]);
};

/**
 * Sets double height and width text
 */
export const textDoubleHeightWidth = (): Uint8Array => {
    return new Uint8Array([ESC, 0x21, 0x30]);
};

/**
 * Resets text size to normal
 */
export const textNormalSize = (): Uint8Array => {
    return new Uint8Array([ESC, 0x21, 0x00]);
};

/**
 * Adds horizontal rule
 */
export const horizontalRule = (): Uint8Array => {
    return textToUint8Array("------------------------------------------------");
};

/**
 * Adds dashed horizontal rule
 */
export const dashedRule = (): Uint8Array => {
    return textToUint8Array("- - - - - - - - - - - - - - - - - - - - - - - -");
};

/**
 * Adds a line feed
 */
export const lineFeed = (): Uint8Array => {
    return new Uint8Array([LF]);
};

/**
 * Adds multiple line feeds
 */
export const lineFeeds = (n: number): Uint8Array => {
    const result = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
        result[i] = LF;
    }
    return result;
};

/**
 * Cuts the paper
 */
export const cutPaper = (): Uint8Array => {
    return new Uint8Array([GS, 0x56, 0x00]);
};

/**
 * Converts a string to a Uint8Array
 */
export const textToUint8Array = (text: string): Uint8Array => {
    const encoder = new TextEncoder();
    return encoder.encode(text);
};

/**
 * Concatenates multiple Uint8Arrays
 */
export const concatUint8Arrays = (...arrays: Uint8Array[]): Uint8Array => {
    const totalLength = arrays.reduce((acc, array) => acc + array.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;

    for (const array of arrays) {
        result.set(array, offset);
        offset += array.length;
    }

    return result;
};

/**
 * Formats a number as currency with BDT symbol
 * Using 'Tk' instead of Unicode BDT symbol for better printer compatibility
 * Displays as integer (no decimal digits)
 */
export const formatCurrency = (amount: number): string => {
    return "Tk " + Math.round(amount).toString();
};

/**
 * Generates ESC/POS commands for a receipt
 */
export const generateReceiptCommands = (
    items: Item[],
    storeDetails: StoreDetails,
    deliveryCharge: DeliveryCharge,
    discount: Discount,
    logoCommands?: Uint8Array,
    footerRaster?: Uint8Array
): Uint8Array => {
    const commands: Uint8Array[] = [];

    // Initialize printer
    commands.push(new Uint8Array([ESC, 0x40])); // Initialize printer
    commands.push(new Uint8Array([ESC, 0x74, 0x00])); // Select character code table

    // If logo provided, print it first
    if (logoCommands && logoCommands.length > 0) {
        commands.push(textLeft());
        commands.push(logoCommands);
        commands.push(lineFeed());
    }

    // Store header (skip if a prebuilt header raster was provided)
    if (!logoCommands) {
        commands.push(textCenter());
        commands.push(textDoubleHeightWidth());
        commands.push(textBold());
        commands.push(textToUint8Array(storeDetails.name));
        commands.push(lineFeed());
        commands.push(textNormalSize());
        commands.push(textNormal());
        commands.push(textToUint8Array(storeDetails.address));
        commands.push(lineFeed());
        commands.push(textToUint8Array(storeDetails.phone));
        commands.push(lineFeed());
        if (storeDetails.email) {
            commands.push(textToUint8Array(storeDetails.email));
            commands.push(lineFeed());
        }
        if (storeDetails.website) {
            commands.push(textToUint8Array(storeDetails.website));
            commands.push(lineFeed());
        }
    }

    // Receipt number and date/time
    const now = new Date();
    // const receiptNumber = `R-${Math.floor(100000 + Math.random() * 900000)}`;
    // commands.push(lineFeeds(2));
    commands.push(textLeft());
    // commands.push(textBold());
    // commands.push(textToUint8Array(`Receipt: ${receiptNumber}`));
    commands.push(textNormal());
    commands.push(lineFeed());
    commands.push(textToUint8Array(`Date: ${now.toLocaleDateString()}`));
    commands.push(lineFeed());
    commands.push(textToUint8Array(`Time: ${now.toLocaleTimeString()}`));
    commands.push(lineFeed());
    commands.push(horizontalRule());
    commands.push(lineFeed());

    // Items
    // commands.push(textLeft());
    // commands.push(textBold());
    // commands.push(textToUint8Array("Items"));
    // commands.push(textNormal());
    // commands.push(lineFeed());
    // commands.push(dashedRule());
    // commands.push(lineFeed());

    let subtotal = 0;

    for (const item of items) {
        const itemTotal = item.quantity * item.price;
        subtotal += itemTotal;

        // Item name on its own row
        commands.push(textBold());
        commands.push(textToUint8Array(item.name));
        commands.push(textNormal());
        commands.push(lineFeed());

        // Quantity, price, and total on the next row - just values, no labels
        const detailsLine = `${item.quantity} x ${formatCurrency(
            item.price
        )} = ${formatCurrency(itemTotal)}`;
        commands.push(textToUint8Array(`>> ${detailsLine}`)); // Indent details line
        commands.push(lineFeed());

        // Add a small separator between items
        commands.push(lineFeed());
    }

    // Remove the last extra line feed if there were any items
    if (items.length > 0) {
        commands.pop();
    }

    // Totals
    commands.push(dashedRule());
    commands.push(lineFeed());

    commands.push(
        textToUint8Array(
            `Subtotal:${formatCurrency(subtotal).padStart(34, " ")}`
        )
    );
    commands.push(lineFeed());

    // Only show discount if amount is greater than 0
    if (discount.amount > 0) {
        commands.push(
            textToUint8Array(
                `${discount.label}:${formatCurrency(discount.amount).padStart(
                    40 - discount.label.length,
                    " "
                )}`
            )
        );
        commands.push(lineFeed());
    }

    // Calculate discounted subtotal
    const discountedSubtotal = subtotal - discount.amount;

    // Only show delivery charge if amount is greater than 0
    if (deliveryCharge.amount > 0) {
        commands.push(
            textToUint8Array(
                `${deliveryCharge.label}:${formatCurrency(
                    deliveryCharge.amount
                ).padStart(40 - deliveryCharge.label.length, " ")}`
            )
        );
        commands.push(lineFeed());
    }

    // Calculate final total
    const total = discountedSubtotal + deliveryCharge.amount;

    commands.push(horizontalRule());
    commands.push(lineFeed());
    commands.push(textBold());
    commands.push(textDoubleWidth());
    // bring the total at the center
    commands.push(textCenter());
    commands.push(
        textToUint8Array(`${formatCurrency(total).padStart(2, " ")}`)
    );
    commands.push(textNormalSize());
    commands.push(textNormal());
    commands.push(lineFeeds(2));

    // Footer
    commands.push(textCenter());
    // Keep footer compact
    commands.push(textNormal());
    // Optional follow footer raster (QR + message)
    if (footerRaster && footerRaster.length > 0) {
        commands.push(footerRaster);
    }
    commands.push(textToUint8Array("Thank you for your purchase!"));
    commands.push(lineFeeds(8)); // Add more line feeds before cutting to ensure footer is not cut off

    // Cut paper
    commands.push(cutPaper());

    return concatUint8Arrays(...commands);
};
